class AnimatedImageEffect {
  constructor(options = {}) {
    this.config = {
      selector: '.optimized-img__wrapper',
      loadedClass: 'loaded',
      errorClass: 'load-failed',
      delay: 300,
      enableLogging: false,
      autoDetectChanges: true, // New option
      ...options,
    };

    this.elements = new Set();
    this.observer = null;
    this.mutationObserver = null;

    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleMutations = this.handleMutations.bind(this);
    this.init();
  }

  init() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.handleIntersection, {
        rootMargin: '50px',
        threshold: 0.1,
      });
    }

    // Set up MutationObserver to detect DOM changes
    if (this.config.autoDetectChanges && 'MutationObserver' in window) {
      this.mutationObserver = new MutationObserver(this.handleMutations);
      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    // Initial scan
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.scan());
    } else {
      this.scan();
    }
  }

  // Handle DOM mutations (new elements added)
  handleMutations(mutations) {
    let shouldScan = false;

    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        // Check if any added nodes contain our target elements
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.matches && node.matches(this.config.selector)) {
              shouldScan = true;
            } else if (node.querySelector && node.querySelector(this.config.selector)) {
              shouldScan = true;
            }
          }
        });
      }
    });

    if (shouldScan) {
      // Debounce the scan to avoid excessive calls
      clearTimeout(this.scanTimeout);
      this.scanTimeout = setTimeout(() => this.scan(), 100);
    }
  }

  scan() {
    const elements = document.querySelectorAll(this.config.selector);

    elements.forEach((el) => {
      if (!this.elements.has(el)) {
        this.elements.add(el);

        if (this.observer) {
          this.observer.observe(el);
        } else {
          this.loadImage(el);
        }
      }
    });

    // Clean up removed elements
    this.cleanupRemovedElements();
  }

  // Clean up elements that are no longer in the DOM
  cleanupRemovedElements() {
    const elementsToRemove = [];

    this.elements.forEach((el) => {
      if (!document.contains(el)) {
        elementsToRemove.push(el);
      }
    });

    elementsToRemove.forEach((el) => {
      this.elements.delete(el);
      if (this.observer) {
        this.observer.unobserve(el);
      }
    });
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.loadImage(entry.target);
        this.observer.unobserve(entry.target);
      }
    });
  }

  loadImage(wrapper) {
    const img = wrapper.querySelector('img');
    const sources = wrapper.querySelectorAll('source');

    if (!img) return;

    // Load sources for <picture>
    sources.forEach((source) => {
      if (source.dataset.srcset) {
        source.srcset = source.dataset.srcset;
      }
    });

    // Swap high-res main image
    if (img.dataset.src) {
      img.src = img.dataset.src;
    }

    img.addEventListener(
      'load',
      () => {
        setTimeout(() => {
          wrapper.classList.add(this.config.loadedClass);
        }, this.config.delay);
      },
      { once: true }
    );

    img.addEventListener(
      'error',
      () => {
        wrapper.classList.add(this.config.errorClass);
      },
      { once: true }
    );
  }

  // Public method to manually trigger a rescan
  refresh() {
    this.scan();
  }

  // Method to add specific elements
  addElement(element) {
    if (element && element.matches && element.matches(this.config.selector)) {
      if (!this.elements.has(element)) {
        this.elements.add(element);

        if (this.observer) {
          this.observer.observe(element);
        } else {
          this.loadImage(element);
        }
      }
    }
  }

  // Cleanup method
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    clearTimeout(this.scanTimeout);
    this.elements.clear();
  }
}

// Initialize with automatic change detection
window.animatedImageEffect = new AnimatedImageEffect({
  enableLogging: true,
  autoDetectChanges: true,
});
